/**
 * Application-level constants.
 * Centralized static data used across components.
 */

// ─── Website Options (used in search forms) ─────────────────
export const WEBSITE_OPTIONS = [
  { value: 'allwebsite', label: 'All Websites' },
  { value: 'amazon', label: 'Amazon' },
  { value: 'flipkart', label: 'Flipkart' },
  { value: 'ebay', label: 'eBay' },
  { value: 'snapdeal', label: 'Snapdeal' },
  { value: 'amitretail', label: 'Amit Retail' },
  { value: 'noon', label: 'Noon' },
  { value: 'sharafdg', label: 'Sharaf DG' },
  { value: 'ntsuae', label: 'NTS UAE' },
  { value: 'seazoneuae', label: 'Seazone UAE' },
  { value: 'empiremarine', label: 'Empire Marine' },
  { value: 'climaxmarine', label: 'Climax Marine' },
  { value: 'shopify', label: 'Shopify Store Scanner' },
];

// ─── Amazon Region Options ──────────────────────────────────
export const AMAZON_REGIONS = [
  { value: 'amazon.com', label: 'Amazon.com (US)' },
  { value: 'amazon.co.uk', label: 'Amazon UK' },
  { value: 'amazon.in', label: 'Amazon India' },
  { value: 'amazon.ae', label: 'Amazon UAE' },
  { value: 'amazon.de', label: 'Amazon Germany' },
  { value: 'amazon.fr', label: 'Amazon France' },
  { value: 'amazon.it', label: 'Amazon Italy' },
  { value: 'amazon.es', label: 'Amazon Spain' },
  { value: 'amazon.ca', label: 'Amazon Canada' },
  { value: 'amazon.com.au', label: 'Amazon Australia' },
  { value: 'amazon.sg', label: 'Amazon Singapore' },
  { value: 'amazon.sa', label: 'Amazon Saudi Arabia' },
  { value: 'amazon.nl', label: 'Amazon Netherlands' },
  { value: 'amazon.pl', label: 'Amazon Poland' },
  { value: 'amazon.se', label: 'Amazon Sweden' },
  { value: 'amazon.co.jp', label: 'Amazon Japan' },
  { value: 'amazon.com.br', label: 'Amazon Brazil' },
  { value: 'amazon.com.mx', label: 'Amazon Mexico' },
];

// ─── Ticket Status Options ──────────────────────────────────
export const TICKET_STATUSES = ['open', 'in_progress', 'resolved', 'closed'];

// ─── Ticket Urgency Levels ──────────────────────────────────
export const TICKET_URGENCIES = ['low', 'medium', 'high', 'critical'];

// ─── File Upload Constants ──────────────────────────────────
export const VALID_FILE_EXTENSIONS = ['.csv', '.xlsx', '.xls'];
export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
export const BULK_UPLOAD_TIMEOUT_MS = 300000;

// ─── Scrape Loader Timing Constants ─────────────────────────
export const SCRAPE_LOADER_STEP_INTERVAL_MS = 5000;
export const SCRAPE_LOADER_FAST_INTERVAL_MS = 1000;
export const SCRAPE_LOADER_ALMOST_THERE_DURATION_MS = 3000;

// ─── Attachment File Types ──────────────────────────────────
export const ACCEPTED_ATTACHMENT_TYPES = '.jpg,.jpeg,.png,.pdf,.doc,.docx,.txt';

// ─── Ticket Description Editor Toolbar ──────────────────────
export const TICKET_DESCRIPTION_TOOLBAR = [
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean'],
];

// ═══════════════════════════════════════════════════════════════
// PAGE & COMPONENT STRING CONSTANTS
// ═══════════════════════════════════════════════════════════════

// ─── Common / Shared Strings ────────────────────────────────
export const COMMON = {
  BRAND_NAME: 'TutoMart',
  CANCEL: 'Cancel',
  REFRESH: 'Refresh',
  DELETE: 'Delete',
  LOADING: 'Loading...',
  NA: 'N/A',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

// ─── User Login Page ────
export const USER_LOGIN_PAGE = {
  BRAND_TAGLINE: 'Easy Pricing',
  LOGO_ALT: 'TutoMart Logo',
  TABS: {
    LOGIN: 'Login',
    SIGNUP: 'Sign Up',
  },
  TITLES: {
    SIGN_IN: 'Sign In to TutoMart',
    CREATE_ACCOUNT: 'Create Account',
  },
  SUBTITLES: {
    SIGN_IN: 'Enter your credentials to access your account',
    CREATE_ACCOUNT: 'Join TutoMart to start your learning journey',
  },
  BUTTONS: {
    GOOGLE: 'Continue with Google',
    LOGIN: 'Log In',
    CREATE_ACCOUNT: 'Create Account',
    LOGGING_IN: 'Logging In...',
    CREATING_ACCOUNT: 'Creating Account...',
  },
  FORM: {
    FULL_NAME: 'Full Name',
    EMAIL: 'Email address',
    PASSWORD: 'Password',
    CONFIRM_PASSWORD: 'Confirm Password',
    SHOW_PASSWORD: 'Show Password',
    REMEMBER_ME: 'Remember me for 30 days',
    FORGOT_PASSWORD: 'Forgot password?',
    DIVIDER: 'OR',
  },
  TOGGLE: {
    NO_ACCOUNT: "Don't have an account yet? ",
    HAS_ACCOUNT: 'Already have an account? ',
    SIGN_UP_LINK: 'Sign up',
    LOGIN_LINK: 'Login',
  },
  FOOTER: {
    PREFIX: 'By continuing, you agree to our ',
    TERMS: 'Terms of Service',
    AND: ' and ',
    PRIVACY: 'Privacy Policy',
  },
  MESSAGES: {
    PASSWORDS_MISMATCH: 'Passwords do not match',
    LOGIN_SUCCESS: 'Login successful! Redirecting...',
    GENERIC_ERROR: 'An error occurred. Please try again.',
    INVALID_CREDENTIALS: 'Invalid credentials',
  },
};

// ─── Admin Login Page ───
export const ADMIN_LOGIN_PAGE = {
  TITLE: 'Admin Dashboard Login',
  FORM: {
    EMAIL_PLACEHOLDER: 'admin@tutomart.com',
    PASSWORD_PLACEHOLDER: '••••••••',
  },
  BUTTONS: {
    LOGIN: 'Login',
    LOGGING_IN: 'Logging in...',
  },
  MESSAGES: {
    LOGIN_FAILED: 'Login failed. Check credentials.',
  },
};

// ─── User Dashboard Page ────────────────────────────────────
export const USER_DASHBOARD_PAGE = {
  SECTION_TITLE: 'Start Your Search',
  ERRORS: {
    OPERATION_FAILED: 'Operation Failed',
    SCRAPE_ERROR: 'An error occurred during scraping',
    SELECT_FILE: 'Please select a file first',
    NO_PRODUCTS: 'No products found in the uploaded file',
    BULK_UPLOAD_FAILED: 'Bulk upload failed. Please check your file format and try again.',
  },
};

// ─── User Profile Page ──────────────────────────────────────
export const USER_PROFILE_PAGE = {
  TITLE: 'Profile Overview',
  SUBTITLE: 'Your account details and search statistics at a glance',
  LABELS: {
    TIMEZONE: 'Timezone',
    MEMBER_SINCE: 'Member Since',
    ACCOUNT_STATUS: 'Account Status',
  },
  STATS: {
    TITLE: 'Search Statistics',
    TOTAL_SEARCHES: 'Total Searches',
    MANUAL_SEARCHES: 'Manual Searches',
    BULK_UPLOADS: 'Bulk Uploads',
    WEBSITES_USED: 'Websites Used',
  },
  SUBSCRIPTION: {
    TITLE: 'Subscription Plan',
    LABELS: {
      PLAN: 'Plan',
      STATUS: 'Status',
      MONTHLY_LIMIT: 'Monthly Limit',
      RENEWAL_DATE: 'Renewal Date',
    },
    STATUS_LABELS: {
      active: 'Active',
      expired: 'Expired',
      trial: 'Trial',
      pending_renewal: 'Pending Renewal',
    },
  },
  USAGE: {
    TITLE: 'Search Usage',
    LABELS: {
      SEARCHES_USED: 'Searches Used',
      SEARCHES_REMAINING: 'Remaining',
      MONTHLY_LIMIT: 'Monthly Limit',
      USAGE_PERCENTAGE: 'Usage This Month',
    },
  },
  TIMEZONE: {
    BUTTON_EDIT: 'Edit',
    BUTTON_SAVE: 'Save',
    BUTTON_SAVING: 'Saving...',
    BUTTON_CANCEL: 'Cancel',
    MESSAGES: {
      SUCCESS: 'Timezone updated successfully.',
      REQUIRED: 'Please select a timezone.',
      GENERIC_ERROR: 'Failed to update timezone. Please try again.',
    },
  },
  PASSWORD: {
    TITLE: 'Change Password',
    BUTTON_OPEN: 'Change Password',
    BUTTON_CANCEL: 'Cancel',
    BUTTON_SUBMIT: 'Update Password',
    BUTTON_SUBMITTING: 'Updating...',
    FIELDS: [
      { key: 'current', label: 'Current Password' },
      { key: 'new',     label: 'New Password' },
      { key: 'confirm', label: 'Confirm New Password' },
    ],
    MESSAGES: {
      SUCCESS: 'Password updated successfully.',
      MIN_LENGTH: 'Password must be at least 8 characters.',
      MISMATCH: 'New passwords do not match.',
      SAME_AS_CURRENT: 'New password must differ from your current password.',
      GENERIC_ERROR: 'Failed to update password. Please try again.',
    },
  },
};

// ─── User History Page ──────────────────────────────────────
export const USER_HISTORY_PAGE = {
  TITLE: 'Search History',
  SUBTITLE: 'Track all your previous searches and bulk uploads',
  BUTTONS: {
    CLEAR_ALL: 'Clear All History',
    VIEW_RESULTS: 'View Results',
    DOWNLOAD_RESULTS: 'Download Results',
  },
  STATS: {
    TOTAL_SEARCHES: 'Total Searches',
    MANUAL_SEARCHES: 'Manual Searches',
    BULK_UPLOADS: 'Bulk Uploads',
  },
  FILTERS: {
    LABEL: 'Filter by Type:',
    ALL: 'All',
    MANUAL: 'Manual',
    BULK: 'Bulk',
    SEARCH_PLACEHOLDER: 'Search in history...',
  },
  TABLE: {
    TYPE: 'Type',
    BRAND_STORE: 'Brand / Store',
    PRODUCT: 'Product',
    OEM_ASIN: 'OEM / ASIN',
    WEBSITE: 'Website',
    RESULTS: 'Results',
    ACTIONS: 'Actions',
    BULK_UPLOAD: 'Bulk Upload',
    ALL_WEBSITES: 'All Websites',
  },
  EMPTY: {
    TITLE: 'No Search History Found',
    FILTER_MESSAGE: 'No results match your filters. Try changing them.',
    DEFAULT_MESSAGE: 'Your search history will appear here after you perform searches.',
  },
  CONFIRM: {
    DELETE_ONE: 'Delete this search history?',
    DELETE_ALL: 'Delete ALL search history? This action cannot be undone.',
  },
  MESSAGES: {
    DELETE_FAILED: 'Failed to delete search history',
    DELETE_ALL_FAILED: 'Failed to delete all history',
    VIEW_RESULTS_FAILED: 'Failed to load results',
    DOWNLOAD_FAILED: 'Failed to download results',
  },
  RESULTS_STATUS: {
    available: 'Available',
    expired: 'Expired',
    no_results: 'No Results',
  },
  RESULTS_UNAVAILABLE: {
    expired: 'These results have expired and are no longer available.',
    no_results: 'No results were saved for this search.',
  },
  VIEW_MODAL_TITLE: (search) => `Results — ${search.product || search.brand || 'Search'}`,
};

// ─── User Support Page ──────────────────────────────────────
export const USER_SUPPORT_PAGE = {
  BUTTONS: {
    BACK: 'Back to Dashboard',
    CREATE_NEW: 'Create New Ticket',
    SUBMIT: 'Submit Ticket',
    CREATING: 'Creating...',
    CREATE_FIRST: 'Create Your First Ticket',
  },
  FORM: {
    TITLE: 'Create New Support Ticket',
    SUBJECT_LABEL: 'Subject',
    SUBJECT_PLACEHOLDER: 'Brief description of your issue',
    URGENCY_LABEL: 'Urgency',
    DESCRIPTION_LABEL: 'Description',
    DESCRIPTION_PLACEHOLDER: 'Please provide detailed information about your issue...',
    ATTACHMENTS_LABEL: 'Attachments (Optional)',
    ATTACHMENTS_HELP: 'Max size: 10MB each. Supported: Images, PDF, Docs.',
    SELECTED_FILES: (count) => `Selected files (${count}):`,
    REMOVE_FILE: 'Remove file',
  },
  TABLE: {
    TITLE: 'My Support Tickets',
    TICKET_ID: 'Ticket ID',
    SUBJECT: 'Subject',
    URGENCY: 'Urgency',
    STATUS: 'Status',
    DATE: 'Date',
    FILES: 'Files',
  },
  EMPTY: {
    TITLE: 'No Support Tickets',
    MESSAGE: "You haven't created any support tickets yet.",
  },
  MESSAGES: {
    TICKET_CREATED: 'Ticket created successfully!',
    CREATE_FAILED: 'Failed to create ticket',
    DESCRIPTION_REQUIRED: 'Please provide a description for your issue.',
  },
};

// ─── User Ticket Detail Page ────────────────────────────────
export const USER_TICKET_DETAIL_PAGE = {
  BUTTONS: {
    GO_BACK: 'Go Back',
    BACK_TO_TICKETS: 'Back to My Tickets',
  },
  LABELS: {
    CREATED: 'Created:',
    ID: 'ID:',
    DESCRIPTION: 'Description',
    ATTACHMENTS: 'Attachments',
    CLICK_TO_DOWNLOAD: 'Click to view/download',
  },
  MESSAGES: {
    LOAD_FAILED: 'Failed to load ticket details.',
  },
};

// ─── Ticket Comments (shared by user & admin ticket detail) ─
export const TICKET_COMMENTS = {
  TITLE: 'Comments',
  PLACEHOLDER: 'Write a comment...',
  INTERNAL_LABEL: 'Internal note (not visible to customer)',
  INTERNAL_BADGE: 'Internal',
  ADMIN_BADGE: 'Support',
  SUBMIT: 'Add Comment',
  SUBMITTING: 'Posting...',
  EMPTY: 'No comments yet.',
  LOAD_FAILED: 'Failed to load comments.',
  POST_FAILED: 'Failed to post comment.',
  CLOSED_MESSAGE: 'Comments are disabled because this ticket is closed.',
};

// ─── Admin Dashboard Page ───────────────────────────────────
export const ADMIN_DASHBOARD_PAGE = {
  TITLE: 'Dashboard Overview',
  WELCOME_PREFIX: 'Welcome back, ',
  SUBTITLES: {
    ADMIN: "Here's your system overview.",
    EMPLOYEE: 'Here are your assigned tickets.',
  },
  BUTTONS: {
    MANAGE_TICKETS: 'Manage Tickets',
    ASSIGN_TICKETS: 'Assign Tickets',
  },
  STATS: {
    TOTAL_TICKETS: 'Total Tickets',
    OPEN_TICKETS: 'Open Tickets',
    TOTAL_USERS: 'Total Users',
    ACTIVE_EMPLOYEES: 'Active Employees',
    IN_PROGRESS: 'In Progress',
    RESOLVED: 'Resolved',
    CRITICAL: 'Critical',
    UNASSIGNED: 'Unassigned',
    CLOSED: 'Closed',
  },
  CHARTS: {
    URGENCY_STATUS_TITLE: 'Tickets by Urgency (Status Breakdown)',
    URGENCY_STATUS_SUBTITLE: 'Stacked bar showing status distribution within each urgency level',
    URGENCY_LEVEL: 'Urgency Level',
    NUMBER_OF_TICKETS: 'Number of Tickets',
    DETAILED_TITLE: 'Detailed Urgency Breakdown',
    DETAILED_SUBTITLE: 'Status distribution for each urgency level',
    STATUS_TITLE: 'Tickets by Status',
    URGENCY_TITLE: 'Tickets by Urgency',
    STATUS_LABELS: ['Open', 'In Progress', 'Resolved', 'Closed'],
    URGENCY_LABELS: ['Critical', 'High', 'Medium', 'Low'],
  },
  TABLE: {
    RECENT_ADMIN: 'Recent Tickets',
    RECENT_EMPLOYEE: 'My Recent Tickets',
    VIEW_ALL: 'View All',
    TICKET_NUMBER: 'Ticket #',
    SUBJECT: 'Subject',
    CUSTOMER: 'Customer',
    ASSIGNED_TO: 'Assigned To',
    URGENCY: 'Urgency',
    STATUS: 'Status',
    CREATED: 'Created',
    ACTION: 'Action',
    UNASSIGNED: 'Unassigned',
    VIEW: 'View',
    NO_TICKETS: 'No tickets found',
  },
  LOADING: 'Loading dashboard...',
  ERROR_TITLE: 'Error Loading Dashboard',
  ERROR_MESSAGE: 'Cannot connect to server (Port 8080). Make sure backend is running.',
};

// ─── Admin Tickets Page ─────────────────────────────────────
export const ADMIN_TICKETS_PAGE = {
  TITLE: 'Support Tickets',
  SUBTITLES: {
    ADMIN: 'Manage all support tickets',
    EMPLOYEE: 'View your assigned tickets',
  },
  BUTTONS: {
    ASSIGN_TICKETS: 'Assign Tickets',
    SEARCH: 'Search',
    VIEW: 'View',
    REPLY: 'Reply',
    ASSIGN: 'Assign',
  },
  SEARCH_PLACEHOLDER: 'Search tickets...',
  TABLE: {
    TICKET_NUMBER: 'Ticket #',
    SUBJECT: 'Subject',
    CUSTOMER: 'Customer',
    URGENCY: 'Urgency',
    STATUS: 'Status',
    ASSIGNED_TO: 'Assigned To',
    CREATED: 'Created',
    ACTIONS: 'Actions',
    UNKNOWN: 'Unknown',
  },
  EMPTY: {
    TITLE: 'No Tickets Found',
    MESSAGE: 'No tickets match the current filter.',
  },
  MODALS: {
    REPLY_TITLE: 'Reply to Ticket',
    REPLY_LABEL: 'Your Reply',
    REPLY_PLACEHOLDER: 'Type your reply here...',
    SEND_REPLY: 'Send Reply',
    ASSIGN_TITLE: 'Assign Ticket',
    SELECT_EMPLOYEE: 'Select Employee',
    CHOOSE_EMPLOYEE: 'Choose employee...',
  },
  MESSAGES: {
    STATUS_UPDATED: 'Ticket status updated successfully!',
    STATUS_UPDATE_FAILED: 'Failed to update ticket status',
    ASSIGNED: 'Ticket assigned successfully!',
    ASSIGN_FAILED: 'Failed to assign ticket',
    NETWORK_ERROR: 'Network error. Please try again.',
    REPLY_ADDED: 'Reply added successfully!',
    REPLY_FAILED: 'Failed to add reply',
    FETCH_FAILED: 'Failed to connect to backend.',
  },
};

// ─── Admin Ticket Detail Page ───────────────────────────────
export const ADMIN_TICKET_DETAIL_PAGE = {
  TITLE: 'Ticket Details',
  BUTTONS: {
    BACK: '← Back to Tickets',
    SEND_REPLY: 'Send Reply',
    SENDING: 'Sending...',
    BACK_ERROR: 'Back to Tickets',
  },
  SECTIONS: {
    USER_INFO: 'User Information',
    TICKET_INFO: 'Ticket Information',
    DESCRIPTION: 'Description',
    ADD_REPLY: 'Add Reply',
    ATTACHMENTS: 'Attachments',
  },
  LABELS: {
    NAME: 'Name:',
    EMAIL: 'Email:',
    USER_ID: 'User ID:',
    CREATED: 'Created:',
    LAST_UPDATED: 'Last Updated:',
    ATTACHMENTS_COUNT: 'Attachments:',
    TICKET_ID: 'Ticket ID:',
    REPLY_PLACEHOLDER: 'Type your reply here...',
  },
  LOADING: 'Loading ticket details...',
  MESSAGES: {
    ERROR_TITLE: 'Error',
    NOT_FOUND: 'Ticket not found or unauthorized',
    FETCH_FAILED: 'Failed to fetch ticket details',
    REPLY_SUCCESS: 'Reply sent successfully!',
    REPLY_FAILED: 'Failed to send reply',
  },
};

// ─── Admin Users Page ───
export const ADMIN_USERS_PAGE = {
  TITLE: 'Registered Users',
  TABLE: {
    TOTAL_USERS: 'Total Users:',
    USER_ID: 'User ID',
    NAME: 'Name',
    EMAIL: 'Email',
    TOTAL_TICKETS: 'Total Tickets',
    STATUS: 'Status',
  },
  EMPTY: 'No registered users found.',
  MESSAGES: {
    FETCH_FAILED: 'Failed to fetch users or unauthorized',
    NETWORK_ERROR: 'Network error. Please check backend connection.',
    ERROR_TITLE: 'Error',
  },
};

// ─── Admin Assign Page ──────────────────────────────────────
export const ADMIN_ASSIGN_PAGE = {
  TITLE: 'Assign Tickets',
  BUTTONS: {
    BULK_ASSIGN: 'Bulk Assign',
    ASSIGN: 'Assign',
    ASSIGN_ALL: 'Assign All',
    ASSIGN_TO: 'Assign To',
    SELECT_ALL: 'Select All',
  },
  STATS: {
    UNASSIGNED: 'Unassigned Tickets',
    ACTIVE_EMPLOYEES: 'Active Employees',
    ACTIVE_ASSIGNED: 'Active Tickets (Assigned)',
  },
  TABLE: {
    UNASSIGNED_QUEUE: 'Unassigned Queue',
    EMPTY: 'No tickets to assign.',
    ID: 'ID',
    SUBJECT: 'Subject',
    CUSTOMER: 'Customer',
    URGENCY: 'Urgency',
    ACTION: 'Action',
  },
  EMPLOYEE_SECTION: {
    TITLE: 'Employee Workload',
  },
  MODALS: {
    ASSIGN_TITLE: 'Assign Ticket',
    BULK_TITLE: 'Bulk Assign',
    SELECT_EMPLOYEE: 'Select Employee...',
  },
  MESSAGES: {
    ASSIGNED_SUCCESS: (name) => `Assigned to ${name}!`,
    ASSIGNED_COUNT: (count) => `Assigned ${count} tickets.`,
    NETWORK_ERROR: 'Network error. Check backend.',
    ASSIGN_FAILED: 'Failed to assign.',
    BULK_FAILED: 'Bulk assign failed',
  },
};

// ─── Admin Employees Page ───────────────────────────────────
export const ADMIN_EMPLOYEES_PAGE = {
  TITLE: 'Employee Management',
  BUTTONS: {
    ADD_NEW: '+ Add New Employee',
    DEACTIVATE: 'Deactivate',
    ACTIVATE: 'Activate',
    ADD_EMPLOYEE: 'Add Employee',
    DELETE_EMPLOYEE: 'Delete Employee',
  },
  TABLE: {
    NAME: 'Name',
    EMAIL: 'Email',
    STATUS: 'Status',
    TOTAL_TICKETS: 'Total Tickets',
    ACTIVE_TICKETS: 'Active Tickets',
    ACTIONS: 'Actions',
  },
  EMPTY: 'No employees found. Click "Add New Employee" to create one.',
  MODALS: {
    ADD_TITLE: 'Add New Employee',
    ADD_NAME_PLACEHOLDER: 'Enter employee name',
    ADD_EMAIL_PLACEHOLDER: 'employee@tutomart.com',
    ADD_PASSWORD_PLACEHOLDER: 'Enter password',
    DELETE_TITLE: 'Confirm Delete',
    DELETE_CONFIRM: 'Are you sure you want to delete employee',
    DELETE_WARNING: 'This action cannot be undone.',
    DELETE_ACTIVE_WARNING: (count) => `Warning: This employee has ${count} active tickets. These tickets will be unassigned and returned to the queue.`,
  },
  MESSAGES: {
    ADD_FAILED: 'Failed to add employee',
    SERVER_ERROR: 'Failed to connect to server',
    UPDATE_FAILED: 'Failed to update status',
    DELETE_FAILED: 'Failed to delete employee',
  },
};

// ─── Admin Navbar Strings ───────────────────────────────────
export const ADMIN_NAVBAR_STRINGS = {
  ROLES: {
    ADMIN: 'Admin Panel',
    STAFF: 'Staff Portal',
    DEFAULT: 'Support Portal',
    ADMIN_LABEL: 'Administrator',
    STAFF_LABEL: 'Support Staff',
    USER_LABEL: 'User',
  },
  NAV: {
    DASHBOARD: 'Dashboard',
    TICKETS: 'Tickets',
    MANAGEMENT: 'Management',
    USERS: 'Users',
    EMPLOYEES: 'Employees',
    ASSIGN_TICKETS: 'Assign Tickets',
    MY_TICKETS: 'My Tickets',
    LOGOUT: 'Logout',
    VIEW_ALL_TICKETS: 'View All Tickets',
  },
};

// ─── User Navbar Strings ────────────────────────────────────
export const USER_NAVBAR_STRINGS = {
  WELCOME: 'Welcome back,',
  NAV: {
    DASHBOARD: 'Dashboard',
    SUPPORT: 'Support',
    HISTORY: 'History',
    PROFILE: 'Profile',
    LOGOUT: 'Logout',
  },
};

// ─── Hero Section Strings ───────────────────────────────────
export const HERO_SECTION_STRINGS = {
  GREETING_PREFIX: 'Welcome back, ',
  TITLE_LINE1: 'Find the Best Deals',
  TITLE_LINE2: 'Across All Stores',
  SUBTITLE: 'Compare prices in real-time from Amazon, eBay, Shopify, and more — all in one search.',
  SEARCH_PLACEHOLDER: 'Search any product… e.g. iPhone 15 Pro',
  SEARCH_BUTTON: 'Search Now',
  TRY_LABEL: 'Try:',
  FILTER_BY_STORE: 'Quick filter by store',
  ADVANCED_SEARCH_LABEL: 'Advanced Search',
  EXAMPLES: ['iPhone 15 Pro', 'Samsung Galaxy S24', 'Nike Air Max', 'MacBook Air'],
};

// ─── Product Search Card Strings ────────────────────────────
export const PRODUCT_SEARCH_CARD_STRINGS = {
  TITLE: 'Single Product Search',
  DESCRIPTION: 'Search for individual products across multiple platforms',
  LABELS: {
    WEBSITE: 'Website',
    STORE_URL: 'Store URL',
    BRAND: 'Brand',
    PRODUCT: 'Product',
    OEM_NUMBER: 'OEM Number',
    ASIN_NUMBER: 'ASIN Number',
    AMAZON_REGION: 'Amazon Region',
  },
  PLACEHOLDERS: {
    STORE_URL: 'e.g. gymshark.com, colourpop.com',
    BRAND: 'e.g., Samsung, Apple, Sony',
    PRODUCT: 'e.g., Smart TV, iPhone, Headphones',
    OEM: 'OEM12345',
    ASIN: 'B0CXYZ123',
  },
  HELP: {
    STORE_URL: 'Enter the home page URL of the Shopify store to scan all products.',
    AMAZON_REGION: 'Select which Amazon country you want to scrape. Used when scraping Amazon or All Websites.',
    IDENTIFIER: 'Have an ASIN or OEM number? You can search with just that — Brand and Product are optional then.',
  },
  ERRORS: {
    MISSING_IDENTIFIER: 'Enter a Brand and Product, or at least an OEM Number or ASIN Number, to search.',
  },
  BUTTONS: {
    SEARCHING: 'Searching Products...',
    START: 'Start Price Comparison',
  },
};

// ─── Bulk Upload Card Strings ───────────────────────────────
export const BULK_UPLOAD_CARD_STRINGS = {
  TITLE: 'Bulk Product Analysis',
  DESCRIPTION: 'Upload multiple products at once for comprehensive comparison',
  UPLOAD: {
    TITLE: 'Drag & Drop Your File',
    DESCRIPTION: 'Supported formats: CSV, Excel (.csv, .xlsx, .xls)',
    BROWSE: 'Browse Files',
    AMAZON_REGION: 'Amazon Region',
    AMAZON_HELP: 'Select which Amazon country you want to scrape for bulk upload.',
  },
  GUIDE: {
    TITLE: 'File Format Requirements',
    ITEMS: [
      'Include columns: Brand, Product, Website Name',
      'Optional columns: OEM Number, ASIN Number',
      'First row should contain headers',
      'Supported formats: CSV, XLSX, XLS',
    ],
  },
  BUTTONS: {
    PROCESSING: 'Processing Bulk Upload...',
    ANALYZE: 'Analyze Multiple Products',
  },
  ERRORS: {
    INVALID_FORMAT: 'Please upload a CSV or Excel file (.csv, .xlsx, .xls)',
    FILE_TOO_LARGE: 'File size should be less than 10MB',
  },
};

// ─── Chat Widget Strings ────────────────────────────────────
export const CHAT_WIDGET_STRINGS = {
  WELCOME: 'Hello! I can check live prices or analyze your past searches. How can I help?',
  LAUNCHER: 'AI Assistant',
  HEADER: 'TutoMart AI',
  PLACEHOLDER: 'Ask about prices...',
  ERRORS: {
    CONNECTION_LOST: 'Sorry, I lost connection to the server.',
    GENERIC: 'Sorry, I encountered an error.',
  },
};

// ─── Results Section Strings ────────────────────────────────
export const RESULTS_SECTION_STRINGS = {
  TITLE: 'Comparison Results',
  SUBTITLE: (filtered, total) => `Showing ${filtered} of ${total} products`,
  MATCH: {
    FUZZY: 'Fuzzy Match',
    EXACT: 'Exact Match',
    FUZZY_TITLE: 'Show all related results',
    EXACT_TITLE: 'Show only results that contain Brand + Product Name',
  },
  BUTTONS: {
    EXPORT: 'Export CSV',
  },
  FILTERS: {
    TITLE: 'Filters',
    CLEAR_ALL: 'Clear all',
    SEARCH_LABEL: 'Search',
    SEARCH_PLACEHOLDER: 'Search products...',
    WEBSITE_LABEL: 'Website',
    WEBSITE_ALL: 'All',
    BRAND_LABEL: 'Brand',
    BRAND_ALL: 'All Brands',
    PRICE_RANGE_LABEL: 'Price Range',
    MIN_PRICE_PLACEHOLDER: (min) => `Min (${min})`,
    MAX_PRICE_PLACEHOLDER: (max) => `Max (${max})`,
    MATCH_TYPE_LABEL: 'Match Type',
    MATCH_FUZZY: 'Fuzzy',
    MATCH_EXACT: 'Exact',
    HAS_SELLER_RATING: 'Has seller rating',
    HAS_PRODUCT_IMAGE: 'Has product image',
  },
  TOP_BAR: {
    BACK: 'Back to Search',
    OF_PRODUCTS: (total) => `of ${total} products`,
  },
  CONTROLS: {
    SORT_LABEL: 'Sort by',
    SORT_DEFAULT: 'Default',
    SORT_PRICE_ASC: 'Price: Low to High',
    SORT_PRICE_DESC: 'Price: High to Low',
    SORT_NAME_ASC: 'Name: A to Z',
    SORT_RATING_DESC: 'Rating: High to Low',
    VIEW_GRID: 'Grid view',
    VIEW_LIST: 'List view',
  },
  CARD: {
    UNKNOWN_BRAND: 'Unknown Brand',
    PRODUCT: 'Product',
    OEM: 'OEM',
    ASIN: 'ASIN',
    VIEW_PRODUCT: 'View Product',
  },
  EMPTY: {
    TITLE: 'No results found',
    RESET_BUTTON: 'Reset all filters',
    EXACT_NO_MATCH: "No exact matches found. Try switching to 'Fuzzy Match' to see all results.",
    FILTER_NO_MATCH: 'No products match your filters.',
  },
  EXPORT_FILENAME: 'price_comparison_results.csv',
  CSV_HEADERS: [
    'BRAND', 'PRODUCT', 'OEM NUMBER', 'ASIN NUMBER', 'WEBSITE',
    'PRODUCT NAME', 'PRICE', 'CURRENCY', 'SELLER RATING',
    'DATE SCRAPED', 'SOURCE URL', 'IMAGE',
  ],
};


// ─── Assign Ticket Modal Strings ────────────────────────────
export const ASSIGN_TICKET_MODAL_STRINGS = {
  SELECT_EMPLOYEE: 'Select Employee',
  CHOOSE_EMPLOYEE: 'Choose employee...',
  ASSIGNING: 'Assigning...',
  ASSIGN_FAILED: 'Failed to assign ticket',
};

// ─── Forgot Password Page ───────────────────────────────────
export const FORGOT_PASSWORD_PAGE = {
  LOGO_ALT: 'TutoMart Logo',
  TITLE: 'Reset Password',
  TAGLINE: 'Enter your email to receive a secure reset link',
  BACK_BUTTON: 'Back to Login',
  FORM: {
    EMAIL: 'Email address',
  },
  BUTTONS: {
    SUBMIT: 'Send Reset Link',
    SUBMITTING: 'Sending Link...',
  },
  MESSAGES: {
    SUCCESS: 'If an account exists, a reset link has been sent to your email.',
    ERROR_DEFAULT: 'Failed to request password reset. Please try again.',
    ERROR_NETWORK: 'Network error. Could not connect to the server.',
  },
};

// ─── Reset Password Page ────────────────────────────────────
export const RESET_PASSWORD_PAGE = {
  LOGO_ALT: 'TutoMart Logo',
  TITLE: 'Create New Password',
  TAGLINE: 'Please enter your new secure password below',
  FORM: {
    NEW_PASSWORD: 'New Password',
    CONFIRM_PASSWORD: 'Confirm New Password',
  },
  BUTTONS: {
    SUBMIT: 'Reset Password',
    SUBMITTING: 'Saving...',
    GO_TO_LOGIN: 'Go to Login',
  },
  MESSAGES: {
    SUCCESS: 'Password successfully reset! You can now log in.',
    ERROR_MISMATCH: 'Passwords do not match.',
    ERROR_INVALID_TOKEN: 'Invalid or missing reset token. Please request a new link.',
    ERROR_DEFAULT: 'Failed to reset password. The link may have expired.',
    ERROR_NETWORK: 'Network error. Could not connect to the server.',
  },
};
